/** @jsx jsx */
import type { PropsWithChildren, FC } from 'react';
import { jsx, cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';

const ReplyCommentWrapper: FC<
	PropsWithChildren<{
		isFirst?: boolean;
		isLast?: boolean;
		hasReplyEditor?: boolean;
		showNextButton?: boolean;
	}>
> = ({
	children,
	isFirst = false,
	isLast = false,
	hasReplyEditor = false,
	showNextButton = false,
}) => {
	return (
		<section
			data-testid="issue-view-activity-comment.comment-reply-wrapper.reply-container"
			css={[
				sectionStylesOverride.common,
				// apply style to the first section
				isFirst && !isLast && sectionStylesOverride.firstSection,
				// apply style to sections b/w first and last section
				!isFirst && !isLast && sectionStylesOverride.InBetweenSections,
				// when last button + editor is opened [edge case] focussed comment case
				showNextButton && hasReplyEditor && sectionStylesOverride.InBetweenSections,
				fg('jira_custom_comment_component_refactor') && sectionStylesOverride.customComment,
				hasReplyEditor &&
					fg('jira_custom_comment_component_refactor') &&
					sectionStylesOverride.customCommentEditor,
			]}
		>
			{children}
		</section>
	);
};

const sectionStylesOverride = cssMap({
	common: {
		position: 'relative',
		'--commentLineWidth': '1px',
		'--commentSpacing': token('space.150'),
		'--commentLineColor': token('color.background.accent.gray.subtler'),
		'--commentInlineStart': 'calc(-26px - var(--commentLineWidth)/2)',
	},
	// first of type and not last of type
	firstSection: {
		position: 'relative',
		'&::before': {
			content: '',
			position: 'absolute',
			height: 'calc(100% - var(--commentSpacing))',
			top: 'var(--commentSpacing)',
			width: 'var(--commentLineWidth)',
			insetInlineStart: 'var(--commentInlineStart)',
			backgroundColor: 'var(--commentLineColor)',
		},
	},
	InBetweenSections: {
		position: 'relative',
		'&::before': {
			content: '',
			position: 'absolute',
			bottom: token('space.0'),
			width: 'var(--commentLineWidth)',
			insetInlineStart: 'var(--commentInlineStart)',
			backgroundColor: 'var(--commentLineColor)',
			height: 'calc(100% - var(--commentSpacing))',
			top: 'var(--commentSpacing)',
		},
	},
	customComment: {
		'&::before': {
			height: '100%',
			insetInlineStart: '-14px',
		},
	},
	customCommentEditor: {
		marginLeft: 'calc(var(--commentSpacing) +  var(--commentLineWidth)/2)',
	},
});

export default ReplyCommentWrapper;
